import React, { useMemo } from 'react';
import {
  format,
  addDays,
  addMonths,
  startOfMonth,
  startOfWeek,
  startOfYear,
  endOfMonth,
  isBefore,
  isEqual,
  parseISO,
} from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Typography, theme } from 'antd';

const { Title } = Typography;
const { useToken } = theme;

const COLORS = {
  calls: '#1890ff',
  sms: '#52c41a',
  data: '#faad14',
};

export type UsageBucket = {
  period: string; // "2026-04-01" or "2026-04"
  call_count: number | string;
  sms_count: number | string;
  data_count: number | string;
  total_call_duration: number | string;
  total_data_volume: number | string;
};

type Period = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | `year_${number}`;

type ChartPoint = {
  key: string;
  label: string;
  calls: number;
  sms: number;
  data: number;
};

const getGroupingLevel = (period: Period): 'day' | 'month' => {
  if (period.startsWith('year_') || period === 'thisYear') return 'month';
  return 'day';
};

/** Generate all expected date slots between start and end at the given granularity */
const generateDateSlots = (period: Period): ChartPoint[] => {
  const groupBy = getGroupingLevel(period);
  const now = new Date();
  let start: Date;
  let end: Date;

  if (period.startsWith('year_')) {
    const year = parseInt(period.replace('year_', ''));
    start = new Date(year, 0, 1);
    end = new Date(year, 11, 31);
  } else {
    switch (period) {
      case 'thisWeek':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = now;
        break;
      case 'thisMonth':
        start = startOfMonth(now);
        end = now;
        break;
      case 'lastMonth': {
        const lm = startOfMonth(addMonths(now, -1));
        start = lm;
        end = endOfMonth(lm);
        break;
      }
      case 'thisYear':
        start = startOfYear(now);
        end = now;
        break;
      default:
        start = startOfMonth(now);
        end = now;
    }
  }

  const slots: ChartPoint[] = [];
  let current = start;
  const addFn = groupBy === 'month' ? (d: Date) => addMonths(d, 1) : (d: Date) => addDays(d, 1);
  const fmtLabel =
    groupBy === 'month' ? (d: Date) => format(d, 'MMM') : (d: Date) => format(d, 'MMM d');
  const fmtKey =
    groupBy === 'month' ? (d: Date) => format(d, 'yyyy-MM') : (d: Date) => format(d, 'yyyy-MM-dd');

  while (isBefore(current, end) || isEqual(current, end)) {
    slots.push({ key: fmtKey(current), label: fmtLabel(current), calls: 0, sms: 0, data: 0 });
    const next = addFn(current);
    if (isEqual(next, current)) break; // safety
    current = next;
  }

  return slots;
};

const formatKilobytes = (kb: number) => {
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let value = kb;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return {
    value,
    unit: units[unitIndex],
    divisor: Math.pow(1024, unitIndex),
  };
};

const parseNum = (val: unknown): number =>
  typeof val === 'string' ? parseFloat(val) : (val as number) || 0;

const SimUsageGraph = ({
  buckets,
  period = 'thisMonth',
  metrics = ['calls', 'sms', 'data'],
}: {
  buckets: UsageBucket[];
  period: Period;
  /** Which metric charts to render. Omit a metric entirely when that data type
   *  is not available for the SIM (e.g. Data SIMs have no calls or SMS). */
  metrics?: Array<'calls' | 'sms' | 'data'>;
}) => {
  const { token } = useToken();

  const chartData = useMemo(() => {
    const groupBy = getGroupingLevel(period);
    const slots = generateDateSlots(period);

    // Build a lookup from label → slot index
    const slotMap = new Map<string, number>();
    slots.forEach((slot, i) => slotMap.set(slot.key, i));

    // Merge bucket data into slots
    for (const bucket of buckets) {
      const key = String(bucket.period).slice(0, groupBy === 'month' ? 7 : 10);
      const idx = slotMap.get(key);
      if (idx !== undefined) {
        slots[idx].calls += parseNum(bucket.call_count);
        slots[idx].sms += parseNum(bucket.sms_count);
        slots[idx].data += parseNum(bucket.total_data_volume);
      }
    }

    return slots;
  }, [buckets, period]);

  const getDataWithUnit = (metric: 'calls' | 'sms' | 'data') => {
    if (!chartData.length) return { unit: '', data: [] as ChartPoint[] };

    if (metric === 'data') {
      const maxKB = Math.max(...chartData.map(d => d.data || 0));
      if (maxKB === 0) return { unit: 'KB', data: [] as ChartPoint[] };

      const { unit, divisor } = formatKilobytes(maxKB);
      return {
        unit,
        data: chartData.map(d => ({
          ...d,
          data: (d.data || 0) / divisor,
        })),
      };
    }

    const hasData = chartData.some(d => d[metric] > 0);
    return {
      unit: 'Count',
      data: hasData ? chartData : [],
    };
  };

  const renderChart = (metric: 'calls' | 'sms' | 'data') => {
    const { data, unit } = getDataWithUnit(metric);
    const metricLabel = metric.toUpperCase();

    return (
      <div key={metric} style={{ marginBottom: 40 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          {metricLabel} ({unit})
        </Title>

        {data.length === 0 ? (
          <div
            style={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px dashed ${token.colorBorder}`,
              borderRadius: 8,
              color: token.colorTextSecondary,
              fontStyle: 'italic',
              backgroundColor: token.colorBgContainer,
            }}
          >
            No {metric} recorded for this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis dataKey="label" stroke={token.colorTextSecondary} />
              <YAxis
                stroke={token.colorTextSecondary}
                label={{
                  value: `${metricLabel} (${unit})`,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: token.colorTextSecondary },
                }}
                domain={[0, 'auto']}
              />
              <Tooltip<number, string>
                contentStyle={{
                  background: token.colorBgElevated,
                  border: `1px solid ${token.colorBorder}`,
                  color: token.colorText,
                }}
                labelStyle={{ color: token.colorText }}
                formatter={value => (metric === 'data' ? `${value.toFixed(2)} ${unit}` : value)}
              />
              <Bar dataKey={metric} fill={COLORS[metric]} name={metricLabel} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  };

  return <>{metrics.map(m => renderChart(m))}</>;
};

export default SimUsageGraph;
